import React from "react";
import ConfirmationModal from "../ConfirmationModal.jsx";
import { fmtTime } from "../geotab/geotabFormat.js";
import { INPUT, SECONDARY } from "./Fields.jsx";
import useConsolidationCapture from "../../hooks/useConsolidationCapture.js";
import { captureDriverAnswer } from "../../utils/consolidationCaptures.js";

// Question au consolidateur sous le nom du relevé : qui a conduit, pour payer le bon chauffeur (ou les deux d'une équipe).
const ASK = "flex flex-wrap items-center gap-x-2 gap-y-1 rounded-[4px] border border-slate-200 border-l-[3px] border-l-[#ffc400] bg-white px-2 py-1.5 text-xs text-slate-700";
const CHIP = "rounded-[4px] bg-[#ffc400] px-1.5 text-[11px] font-bold text-[#1f1a05]";
const DONE = "rounded-[4px] bg-white px-1.5 text-[11px] font-bold text-[#0f5c57] ring-1 ring-inset ring-[#0f5c57]";
const LINK = "text-[11.5px] font-bold text-accent underline underline-offset-2 focus-visible:outline-2 focus-visible:outline-accent";

// Un employé payé, ou deux pour une équipe (le deuxième est facultatif après « Autre… »).
function Picker({ employees, team, initial, onConfirm, onCancel }) {
  const [first, setFirst] = React.useState(initial || "");
  const [second, setSecond] = React.useState("");
  const ids = [first, second].filter(Boolean);
  const ready = first && first !== second && (!team || second);
  const choices = <><option value="">Choisir…</option>{employees.map((employee) => <option key={employee.id} value={employee.id}>{employee.name}</option>)}</>;
  return <span className="flex flex-wrap items-center gap-1.5">
    <select aria-label="Chauffeur payé" className={`${INPUT} h-6 !w-auto !py-0`} value={first} onChange={(event) => setFirst(event.target.value)}>{choices}</select>
    <select aria-label={team ? "Deuxième chauffeur payé" : "Deuxième chauffeur payé, pour une équipe"} className={`${INPUT} h-6 !w-auto !py-0`}
      value={second} onChange={(event) => setSecond(event.target.value)}>{choices}</select>
    <button type="button" className={SECONDARY} disabled={!ready} onClick={() => onConfirm(ids)}>Choisir</button>
    {onCancel ? <button type="button" className={LINK} onClick={onCancel}>Retour</button> : null}
  </span>;
}

// Fenêtre de confirmation d'un choix : qui sera payé, et ce que la section Geotab affichera.
function confirmation(check, question, row, employees) {
  const planned = row.driver_name;
  const names = check.employee_ids.map((id) => employees.find((employee) => employee.id === id)?.name || "Employé");
  return {
    dispatch: [`Payer ${planned} ?`, `Les heures de ce relevé seront payées à ${planned}, chauffeur prévu au Dispatch.`
      + (question.geotabName ? ` La section Geotab l’affichera pour la session de ${question.geotabName}.` : "")],
    same: ["Même personne ?", `La session Geotab de ${question.geotabName} est celle de ${planned}. Les heures restent payées à ${planned}`
      + `, et cette session ne sera plus signalée sur les autres relevés de ${planned}.`],
    other: [`Payer ${names[0]} ?`, `Les heures de ce relevé seront payées à ${names[0]} au lieu de ${planned}, prévu au Dispatch.`
      + ` La section Geotab affichera aussi ${names[0]}.`],
    team: [`Payer ${names.join(" et ")} ?`, `Les heures de ce relevé seront payées à ${names[0]} et à ${names[1]}, chacun pour toutes les heures`
      + " du relevé. La section Geotab affichera l’équipe."],
  }[check.answer];
}

export default function DriverCheck({ row, question, employees = [], editable, onAnswer }) {
  const [mode, setMode] = React.useState(null); // « full » après « Non » à un nom écrit autrement ; « pick » après « Autre… »
  const [pending, setPending] = React.useState(null); // choix en attente de confirmation
  React.useEffect(() => { setMode(null); }, [question?.geotabName, row.driver_check]);
  const capture = useConsolidationCapture();
  const record = (outcome, check) => captureDriverAnswer(capture, row, outcome, check, employees);
  const answer = (kind, ids = []) => setPending({ answer: kind, geotab_name: question?.geotabName ?? null,
    geotab_driver_id: question?.geotabDriverId ?? null, employee_ids: ids });
  const close = () => { record("cancelled", pending); setPending(null); };
  const [title, description] = pending && question ? confirmation(pending, question, row, employees) : [];
  const confirm = title ? <ConfirmationModal title={title} eyebrow="Consolidation" tone="accent" cornerRadius="grain" panelSize="narrow"
    contentDensity="compact" description={description} warningMessage="Cliquez ensuite sur Sauvegarder pour conserver ce choix."
    cancelLabel="Annuler" confirmLabel="Confirmer" onCancel={close} onClose={close}
    onConfirm={() => { setPending(null); record("draft_change", pending); onAnswer(pending); }} /> : null;
  if (!question) {
    const check = row.driver_check;
    if (!check) return null;
    const paid = (row.paid_drivers || []).map((driver) => driver.name);
    const [chip, text] = {
      dispatch: ["Chauffeur confirmé", <><b className="text-slate-900">{row.driver_name}</b> a conduit{check.geotab_name ? ` · session Geotab de ${check.geotab_name}` : ""}</>],
      same: ["Même personne", <><b className="text-slate-900">{row.driver_name}</b> = session Geotab de {check.geotab_name}</>],
      other: ["Chauffeur corrigé", <>Heures payées à <b className="text-slate-900">{paid[0]}</b> · prévu au Dispatch : {row.driver_name}</>],
      team: ["Équipe", <>Heures payées à <b className="text-slate-900">{paid.join(" et ")}</b></>],
    }[check.answer] || ["Chauffeur", null];
    return <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-slate-700">
      <span className={DONE}>{chip}</span>
      <span>{text}{check.at ? ` · le ${new Date(check.at).toLocaleDateString("fr-CA", { day: "numeric", month: "short" })}` : ""}</span>
      {editable ? <button type="button" className={LINK} onClick={() => { record("draft_change", null); onAnswer(null); }}>Changer</button> : null}
    </div>;
  }
  const geotab = question.geotabName ? <>Geotab : session de <b className="text-slate-900">{question.geotabName}</b>
    {question.geotabEmployee ? "" : " (absent des Employés)"}{question.trucks ? ` sur le ${question.trucks}` : ""}{question.insteadOf ? ` au lieu du ${question.insteadOf}` : ""}
    {question.from != null ? `, de ${fmtTime(question.from)} à ${fmtTime(question.to)}` : ""}.</> : null;
  if (question.team || mode === "pick") {
    return <div role="alert" className={ASK}>
      <span className={CHIP}>{question.team ? "Équipe à payer" : "Chauffeur à confirmer"}</span>
      <span>{question.team ? <>Le Dispatch nomme l'équipe « {question.planned} ». </> : null}{geotab}{" "}
        {question.team ? "Choisissez les deux chauffeurs payés." : "Choisissez le chauffeur payé, ou les deux d'une équipe."}</span>
      {editable ? <Picker employees={employees} team={question.team} initial={question.geotabEmployee?.id}
        onConfirm={(ids) => answer(ids.length === 2 ? "team" : "other", ids)} onCancel={question.team ? null : () => setMode(null)} /> : null}
      {confirm}
    </div>;
  }
  if (question.spelling && mode !== "full") {
    return <div role="alert" className={ASK}>
      <span className={CHIP}>Nom à vérifier</span>
      <span>{geotab} Même personne que <b className="text-slate-900">{question.planned}</b> ?</span>
      {editable ? <span className="flex flex-wrap gap-1.5">
        <button type="button" className={SECONDARY} onClick={() => answer("same")}>Oui, même personne</button>
        <button type="button" className={SECONDARY} onClick={() => setMode("full")}>Non, quelqu’un d’autre</button>
      </span> : null}
      {confirm}
    </div>;
  }
  return <div role="alert" className={ASK}>
    <span className={CHIP}>Chauffeur à confirmer</span>
    <span>{geotab} Qui a conduit ?</span>
    {editable ? <span className="flex flex-wrap gap-1.5">
      <button type="button" className={SECONDARY} onClick={() => answer("dispatch")}>{question.planned} <span className="font-normal text-slate-500">· Dispatch</span></button>
      {question.geotabEmployee ? <button type="button" className={SECONDARY} onClick={() => answer("other", [question.geotabEmployee.id])}>
        {question.geotabEmployee.name} <span className="font-normal text-slate-500">· Geotab</span></button> : null}
      {question.geotabEmployee && row.employee_id ? <button type="button" className={SECONDARY}
        onClick={() => answer("team", [row.employee_id, question.geotabEmployee.id])}>Les deux</button> : null}
      <button type="button" className={SECONDARY} onClick={() => setMode("pick")}>Autre…</button>
    </span> : null}
    {confirm}
  </div>;
}
